import { useEffect, useState, useCallback } from 'react';
import { useParams, useLocation } from 'wouter';
import { useAuth } from '@/hooks/useAuth';
import AccessDenied from '@/components/AccessDenied';
import { CRM_PERMISSIONS, hasPermission } from '@/lib/crmPermissions';
import { toast } from 'sonner';
import * as Dialog from '@radix-ui/react-dialog';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useCustomerDetail,
  useCustomerUpdate,
  useCustomerStageTransition,
  useFollowupList,
  useFollowupCreate,
  useFollowupUpdate,
  useHandoffList,
  useHandoffCreate,
  useHandoffConfirm,
  useHandoffReject,
  useSourceList,
  useSourceCreate,
  useAssignment,
  useTagList,
  useTagAdd,
  useTagRemove,
  CUSTOMER_STATUS_LABELS,
  CUSTOMER_STATUS_LIST,
  INTENT_LEVEL_LABELS,
  FOLLOWUP_TYPE_LABELS,
  SOURCE_TYPE_LABELS,
  HANDOFF_STATUS_LABELS,
  type CustomerDto,
  type FollowupDto,
  type HandoffDto,
  type SourceDto,
  type AssignmentDto,
  type TagDto,
  type CustomerStatus,
  type IntentLevel,
  type FollowupType,
  type SourceType,
} from '@/hooks/useCrm';
import CustomerAccountPanel from '@/pages/crm/CustomerAccountPanel';
import CustomerIntakeAssessmentPanel from '@/pages/intake-assessment/CustomerIntakeAssessmentPanel';
import CustomerQuoteContractPanel from '@/pages/quote-contract/CustomerQuoteContractPanel';

// ─── Edit Customer Dialog ─────────────────────────────────────────────────────

interface EditCustomerDialogProps {
  customer: CustomerDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (updated: CustomerDto) => void;
}

function EditCustomerDialog({ customer, open, onOpenChange, onSaved }: EditCustomerDialogProps) {
  const { loading, error, update } = useCustomerUpdate();
  const [name, setName] = useState(customer.name);
  const [phone, setPhone] = useState(customer.phone ?? '');
  const [email, setEmail] = useState(customer.email ?? '');
  const [intentLevel, setIntentLevel] = useState<IntentLevel | ''>(customer.intentLevel ?? '');
  const [channelPartnerId, setChannelPartnerId] = useState(customer.channelPartnerId ?? '');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('姓名为必填项');
      return;
    }
    const result = await update(customer.id, {
      expectedVersion: customer.version,
      name: name.trim(),
      phone: phone.trim() || undefined,
      email: email.trim() || undefined,
      intentLevel: intentLevel !== '' ? intentLevel : undefined,
      channelPartnerId: channelPartnerId.trim() || undefined,
    });
    if (result) {
      toast.success('客户信息已更新');
      onSaved(result);
      onOpenChange(false);
    } else if (error) {
      toast.error(error.status === 409 ? '版本冲突（409），请刷新后重试' : error.message);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg bg-card p-6 shadow-lg">
          <Dialog.Title className="mb-4 text-lg font-semibold">编辑客户</Dialog.Title>
          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="edit-name">姓名 *</Label>
              <Input id="edit-name" value={name} onChange={(e) => setName(e.target.value)} required />
            </div>
            <div className="space-y-1">
              <Label htmlFor="edit-phone">手机号</Label>
              <Input id="edit-phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="edit-email">邮箱</Label>
              <Input id="edit-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="edit-intent">意向等级</Label>
              <select
                id="edit-intent"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                value={intentLevel}
                onChange={(e) => setIntentLevel(e.target.value as IntentLevel | '')}
              >
                <option value="">不设置</option>
                {(['high', 'medium', 'low', 'none'] as IntentLevel[]).map((l) => (
                  <option key={l} value={l}>{INTENT_LEVEL_LABELS[l]}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="edit-cp">渠道合作方 ID</Label>
              <Input id="edit-cp" value={channelPartnerId} onChange={(e) => setChannelPartnerId(e.target.value)} />
            </div>
            {error && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error.status === 409 ? '版本冲突（409），请刷新后重试' : error.message}
              </p>
            )}
            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={loading}>{loading ? '保存中…' : '保存'}</Button>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>取消</Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ─── Stage Transition Dialog ──────────────────────────────────────────────────

interface StageTransitionDialogProps {
  customer: CustomerDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTransitioned: (updated: CustomerDto) => void;
}

function StageTransitionDialog({ customer, open, onOpenChange, onTransitioned }: StageTransitionDialogProps) {
  const { loading, error, transition } = useCustomerStageTransition();
  const [nextStatus, setNextStatus] = useState<CustomerStatus>(customer.status);
  const [reason, setReason] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await transition(customer.id, {
      expectedVersion: customer.version,
      nextStatus,
      reason: reason.trim() || undefined,
    });
    if (result) {
      toast.success(`阶段已变更为：${CUSTOMER_STATUS_LABELS[result.status]}`);
      onTransitioned(result);
      onOpenChange(false);
    } else if (error) {
      toast.error(error.status === 409 ? '版本冲突（409），请刷新后重试' : error.message);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg bg-card p-6 shadow-lg">
          <Dialog.Title className="mb-4 text-lg font-semibold">变更客户阶段</Dialog.Title>
          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="next-status">目标阶段</Label>
              <select
                id="next-status"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                value={nextStatus}
                onChange={(e) => setNextStatus(e.target.value as CustomerStatus)}
              >
                {CUSTOMER_STATUS_LIST.map((s) => (
                  <option key={s} value={s}>{CUSTOMER_STATUS_LABELS[s]}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="reason">原因（可选）</Label>
              <Input id="reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="说明变更原因" />
            </div>
            {error && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error.status === 409 ? '版本冲突（409），请刷新后重试' : error.message}
              </p>
            )}
            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={loading}>{loading ? '提交中…' : '确认变更'}</Button>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>取消</Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ─── Create Followup Dialog ───────────────────────────────────────────────────

interface CreateFollowupDialogProps {
  customerId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (followup: FollowupDto) => void;
}

function CreateFollowupDialog({ customerId, open, onOpenChange, onCreated }: CreateFollowupDialogProps) {
  const { loading, error, create } = useFollowupCreate();
  const [content, setContent] = useState('');
  const [followupType, setFollowupType] = useState<FollowupType>('phone_call');
  const [estimatedAmount, setEstimatedAmount] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) {
      toast.error('跟进内容为必填项');
      return;
    }
    const result = await create({
      customerId,
      content: content.trim(),
      followupType,
      estimatedAmount: estimatedAmount.trim() || undefined,
    });
    if (result) {
      toast.success('跟进记录已创建');
      onCreated(result);
      onOpenChange(false);
      setContent('');
      setEstimatedAmount('');
    } else if (error) {
      toast.error(`创建失败：${error.message}`);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg bg-card p-6 shadow-lg">
          <Dialog.Title className="mb-4 text-lg font-semibold">新建跟进记录</Dialog.Title>
          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="followup-type">跟进方式</Label>
              <select
                id="followup-type"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                value={followupType}
                onChange={(e) => setFollowupType(e.target.value as FollowupType)}
              >
                {(Object.keys(FOLLOWUP_TYPE_LABELS) as FollowupType[]).map((t) => (
                  <option key={t} value={t}>{FOLLOWUP_TYPE_LABELS[t]}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="followup-content">跟进内容 *</Label>
              <textarea
                id="followup-content"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                rows={3}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="followup-amount">预估金额</Label>
              <Input
                id="followup-amount"
                value={estimatedAmount}
                onChange={(e) => setEstimatedAmount(e.target.value)}
                placeholder="可选，如 10000.00"
              />
            </div>
            {error && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error.message}</p>
            )}
            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={loading}>{loading ? '提交中…' : '创建'}</Button>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>取消</Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ─── Edit Followup Dialog ─────────────────────────────────────────────────────

interface EditFollowupDialogProps {
  followup: FollowupDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (updated: FollowupDto) => void;
}

function EditFollowupDialog({ followup, open, onOpenChange, onSaved }: EditFollowupDialogProps) {
  const { loading, error, update } = useFollowupUpdate();
  const [content, setContent] = useState(followup.content);
  const [followupType, setFollowupType] = useState<FollowupType>(followup.followupType);
  const [estimatedAmount, setEstimatedAmount] = useState(followup.estimatedAmount ?? '');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await update(followup.id, {
      expectedVersion: followup.version,
      content: content.trim() || undefined,
      followupType,
      estimatedAmount: estimatedAmount.trim() || undefined,
    });
    if (result) {
      toast.success('跟进记录已更新');
      onSaved(result);
      onOpenChange(false);
    } else if (error) {
      toast.error(error.status === 409 ? '版本冲突（409），请刷新后重试' : error.message);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg bg-card p-6 shadow-lg">
          <Dialog.Title className="mb-4 text-lg font-semibold">编辑跟进记录</Dialog.Title>
          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="ef-type">跟进方式</Label>
              <select
                id="ef-type"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                value={followupType}
                onChange={(e) => setFollowupType(e.target.value as FollowupType)}
              >
                {(Object.keys(FOLLOWUP_TYPE_LABELS) as FollowupType[]).map((t) => (
                  <option key={t} value={t}>{FOLLOWUP_TYPE_LABELS[t]}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="ef-content">内容</Label>
              <textarea
                id="ef-content"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                rows={3}
                value={content}
                onChange={(e) => setContent(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ef-amount">预估金额</Label>
              <Input id="ef-amount" value={estimatedAmount} onChange={(e) => setEstimatedAmount(e.target.value)} />
            </div>
            {error && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error.status === 409 ? '版本冲突（409），请刷新后重试' : error.message}
              </p>
            )}
            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={loading}>{loading ? '保存中…' : '保存'}</Button>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>取消</Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ─── Create Handoff Dialog ────────────────────────────────────────────────────

interface CreateHandoffDialogProps {
  customerId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (handoff: HandoffDto) => void;
}

function CreateHandoffDialog({ customerId, open, onOpenChange, onCreated }: CreateHandoffDialogProps) {
  const { loading, error, create } = useHandoffCreate();
  const [toUserId, setToUserId] = useState('');
  const [reason, setReason] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!toUserId.trim()) {
      toast.error('接收人 ID 为必填项');
      return;
    }
    const result = await create({
      customerId,
      toUserId: toUserId.trim(),
      reason: reason.trim() || undefined,
    });
    if (result) {
      toast.success('移交申请已创建');
      onCreated(result);
      onOpenChange(false);
      setToUserId('');
      setReason('');
    } else if (error) {
      toast.error(`创建失败：${error.message}`);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg bg-card p-6 shadow-lg">
          <Dialog.Title className="mb-4 text-lg font-semibold">发起客户移交</Dialog.Title>
          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="to-user-id">接收人 ID *</Label>
              <Input id="to-user-id" value={toUserId} onChange={(e) => setToUserId(e.target.value)} required />
            </div>
            <div className="space-y-1">
              <Label htmlFor="handoff-reason">原因</Label>
              <Input id="handoff-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="可选" />
            </div>
            {error && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error.message}</p>
            )}
            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={loading}>{loading ? '提交中…' : '发起移交'}</Button>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>取消</Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ─── Create Source Dialog ─────────────────────────────────────────────────────

interface CreateSourceDialogProps {
  customerId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (source: SourceDto) => void;
}

function CreateSourceDialog({ customerId, open, onOpenChange, onCreated }: CreateSourceDialogProps) {
  const { loading, error, create } = useSourceCreate();
  const [sourceType, setSourceType] = useState<SourceType>('natural_visit');
  const [detailText, setDetailText] = useState('{}');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let detail: Record<string, unknown>;
    try {
      detail = JSON.parse(detailText) as Record<string, unknown>;
    } catch {
      toast.error('来源详情必须是有效的 JSON');
      return;
    }
    const result = await create(customerId, { sourceType, detail });
    if (result) {
      toast.success('来源记录已创建');
      onCreated(result);
      onOpenChange(false);
      setDetailText('{}');
    } else if (error) {
      toast.error(`创建失败：${error.message}`);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg bg-card p-6 shadow-lg">
          <Dialog.Title className="mb-4 text-lg font-semibold">新建来源记录</Dialog.Title>
          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="source-type">来源类型</Label>
              <select
                id="source-type"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                value={sourceType}
                onChange={(e) => setSourceType(e.target.value as SourceType)}
              >
                {(Object.keys(SOURCE_TYPE_LABELS) as SourceType[]).map((t) => (
                  <option key={t} value={t}>{SOURCE_TYPE_LABELS[t]}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="source-detail">来源详情（JSON）</Label>
              <textarea
                id="source-detail"
                className="w-full rounded-md border bg-background px-3 py-2 font-mono text-sm"
                rows={3}
                value={detailText}
                onChange={(e) => setDetailText(e.target.value)}
              />
            </div>
            {error && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error.message}</p>
            )}
            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={loading}>{loading ? '提交中…' : '创建'}</Button>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>取消</Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ─── Upsert Assignment Dialog ─────────────────────────────────────────────────

interface UpsertAssignmentDialogProps {
  customerId: string;
  current: AssignmentDto | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (assignment: AssignmentDto) => void;
}

function UpsertAssignmentDialog({ customerId, current, open, onOpenChange, onSaved }: UpsertAssignmentDialogProps) {
  const { loading, error, upsert } = useAssignment();
  const [marketUserId, setMarketUserId] = useState(current?.marketUserId ?? '');
  const [marketTeamId, setMarketTeamId] = useState(current?.marketTeamId ?? '');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!marketUserId.trim()) {
      toast.error('市场用户 ID 为必填项');
      return;
    }
    const result = await upsert(customerId, {
      marketUserId: marketUserId.trim(),
      marketTeamId: marketTeamId.trim() || undefined,
    });
    if (result) {
      toast.success('分配信息已保存');
      onSaved(result);
      onOpenChange(false);
    } else if (error) {
      toast.error(`保存失败：${error.message}`);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg bg-card p-6 shadow-lg">
          <Dialog.Title className="mb-4 text-lg font-semibold">设置分配信息</Dialog.Title>
          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="market-user-id">市场用户 ID *</Label>
              <Input id="market-user-id" value={marketUserId} onChange={(e) => setMarketUserId(e.target.value)} required />
            </div>
            <div className="space-y-1">
              <Label htmlFor="market-team-id">市场团队 ID</Label>
              <Input id="market-team-id" value={marketTeamId} onChange={(e) => setMarketTeamId(e.target.value)} placeholder="可选" />
            </div>
            {error && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error.message}</p>
            )}
            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={loading}>{loading ? '保存中…' : '保存'}</Button>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>取消</Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ─── Main CustomerDetailPage ──────────────────────────────────────────────────

export default function CustomerDetailPage() {
  // ── All hooks must be called unconditionally at the top level ──────────────
  const params = useParams<{ customerId: string }>();
  const customerId = params.customerId;
  const [, navigate] = useLocation();
  const { permissions } = useAuth();

  const { loading: detailLoading, fetch: fetchDetail } = useCustomerDetail();
  const [customer, setCustomer] = useState<CustomerDto | null>(null);

  const { fetch: fetchFollowups } = useFollowupList();
  const [followups, setFollowups] = useState<readonly FollowupDto[]>([]);

  const { fetch: fetchHandoffs } = useHandoffList();
  const [handoffs, setHandoffs] = useState<readonly HandoffDto[]>([]);
  const { confirm: confirmHandoff, error: confirmError } = useHandoffConfirm();
  const { reject: rejectHandoff, error: rejectError } = useHandoffReject();

  const { fetch: fetchSources } = useSourceList();
  const [sources, setSources] = useState<readonly SourceDto[]>([]);

  const { get: getAssignment } = useAssignment();
  const [assignment, setAssignment] = useState<AssignmentDto | null>(null);

  const { fetch: fetchTags } = useTagList();
  const [tags, setTags] = useState<readonly TagDto[]>([]);
  const { add: addTag } = useTagAdd();
  const { remove: removeTag } = useTagRemove();
  const [newTag, setNewTag] = useState('');

  // Dialog open states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [stageDialogOpen, setStageDialogOpen] = useState(false);
  const [followupCreateOpen, setFollowupCreateOpen] = useState(false);
  const [editFollowup, setEditFollowup] = useState<FollowupDto | null>(null);
  const [handoffCreateOpen, setHandoffCreateOpen] = useState(false);
  const [sourceCreateOpen, setSourceCreateOpen] = useState(false);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);

  // Derive permission flag after all hooks are called.
  const canRead = hasPermission(permissions, CRM_PERMISSIONS.CUSTOMER_READ);

  const loadAll = useCallback(async () => {
    // Gate API requests: skip when the user lacks read permission.
    if (!customerId || !canRead) return;
    // Issue #341: sub-resource fetches are independent — one transient error
    // (404 assignment, malformed list body, network blip) must not abort the
    // remaining requests or leave the page in a half-loaded state that the
    // global ErrorBoundary catches. Promise.allSettled + per-result null
    // fallback keeps the page tree alive even if a hook throws synchronously.
    const resolveOrNull = <T,>(p: Promise<T | null>): Promise<T | null> =>
      p.catch(() => null);
    const [cR, fR, hR, sR, aR, tR] = await Promise.allSettled([
      resolveOrNull(fetchDetail(customerId)),
      resolveOrNull(fetchFollowups({ customerId })),
      resolveOrNull(fetchHandoffs({ customerId })),
      resolveOrNull(fetchSources(customerId)),
      resolveOrNull(getAssignment(customerId)),
      resolveOrNull(fetchTags(customerId)),
    ]);
    const c = cR.status === 'fulfilled' ? cR.value : null;
    const f = fR.status === 'fulfilled' ? fR.value : null;
    const h = hR.status === 'fulfilled' ? hR.value : null;
    const s = sR.status === 'fulfilled' ? sR.value : null;
    const a = aR.status === 'fulfilled' ? aR.value : null;
    const t = tR.status === 'fulfilled' ? tR.value : null;
    if (c) setCustomer(c);
    if (f) setFollowups(f);
    if (h) setHandoffs(h);
    if (s) setSources(s);
    setAssignment(a);
    if (t) setTags(t);
  }, [canRead, customerId, fetchDetail, fetchFollowups, fetchHandoffs, fetchSources, getAssignment, fetchTags]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const handleAddTag = async () => {
    if (!newTag.trim() || !customerId) return;
    const result = await addTag(customerId, { tag: newTag.trim() });
    if (result) {
      setTags((prev) => [...prev, result]);
      setNewTag('');
    }
  };

  const handleRemoveTag = async (tagId: string) => {
    if (!customerId) return;
    const ok = await removeTag(customerId, tagId);
    if (ok) {
      setTags((prev) => prev.filter((t) => t.id !== tagId));
    }
  };

  const handleConfirmHandoff = async (handoff: HandoffDto) => {
    if (!canRead) return;
    const result = await confirmHandoff(handoff.id, { expectedVersion: handoff.version });
    if (result) {
      toast.success('移交已确认');
      setHandoffs((prev) => prev.map((h) => (h.id === result.id ? result : h)));
    } else if (confirmError) {
      toast.error(confirmError.status === 409 ? '版本冲突（409），请刷新后重试' : confirmError.message);
    }
  };

  const handleRejectHandoff = async (handoff: HandoffDto) => {
    if (!canRead) return;
    const result = await rejectHandoff(handoff.id, { expectedVersion: handoff.version });
    if (result) {
      toast.success('移交已拒绝');
      setHandoffs((prev) => prev.map((h) => (h.id === result.id ? result : h)));
    } else if (rejectError) {
      toast.error(rejectError.status === 409 ? '版本冲突（409），请刷新后重试' : rejectError.message);
    }
  };

  // ── Permission early return — after all hooks ─────────────────────────────────
  if (!canRead) {
    return <AccessDenied resource="客户详情" />;
  }

  if (detailLoading && !customer) {
    return <div className="p-8 text-center text-sm text-muted-foreground">加载中…</div>;
  }

  if (!customer) {
    return (
      <div className="p-8 text-center text-sm text-muted-foreground">
        客户不存在或无权访问。
        <button className="ml-2 text-primary hover:underline" onClick={() => navigate('/crm/customers')}>
          返回列表
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button className="mb-1 text-sm text-muted-foreground hover:underline" onClick={() => navigate('/crm/customers')}>
            ← 返回客户列表
          </button>
          <h2 className="text-xl font-semibold">{customer.name}</h2>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setStageDialogOpen(true)}>
            变更阶段
          </Button>
          <Button variant="outline" size="sm" onClick={() => setEditDialogOpen(true)}>
            编辑
          </Button>
        </div>
      </div>

      {/* Basic Info */}
      <Card className="p-4">
        <h3 className="mb-3 font-medium">基本信息</h3>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <dt className="text-muted-foreground">阶段</dt>
          <dd>
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
              {CUSTOMER_STATUS_LABELS[customer.status]}
            </span>
          </dd>
          <dt className="text-muted-foreground">意向</dt>
          <dd>{customer.intentLevel ? INTENT_LEVEL_LABELS[customer.intentLevel] : '—'}</dd>
          <dt className="text-muted-foreground">渠道合作方 ID</dt>
          <dd className="truncate">{customer.channelPartnerId ?? '—'}</dd>
          <dt className="text-muted-foreground">创建时间</dt>
          <dd>{new Date(customer.createdAt).toLocaleString('zh-CN')}</dd>
          <dt className="text-muted-foreground">更新时间</dt>
          <dd>{new Date(customer.updatedAt).toLocaleString('zh-CN')}</dd>
          <dt className="text-muted-foreground">版本</dt>
          <dd>{customer.version}</dd>
        </dl>
      </Card>

      {/* Tags */}
      <Card className="p-4">
        <h3 className="mb-3 font-medium">标签</h3>
        <div className="flex flex-wrap gap-2">
          {tags.map((t) => (
            <span
              key={t.id}
              className="flex items-center gap-1 rounded-full bg-muted px-3 py-0.5 text-sm"
            >
              {t.tag}
              <button
                className="ml-1 text-muted-foreground hover:text-destructive"
                onClick={() => void handleRemoveTag(t.id)}
                aria-label={`删除标签 ${t.tag}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
        <div className="mt-3 flex gap-2">
          <Input
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            placeholder="输入新标签"
            className="max-w-xs"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void handleAddTag();
              }
            }}
          />
          <Button variant="outline" size="sm" onClick={() => void handleAddTag()}>
            添加
          </Button>
        </div>
      </Card>

      {/* Assignment */}
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <h3 className="font-medium">分配信息</h3>
          <Button variant="outline" size="sm" onClick={() => setAssignmentDialogOpen(true)}>
            {assignment ? '修改分配' : '设置分配'}
          </Button>
        </div>
        {assignment ? (
          <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <dt className="text-muted-foreground">当前负责人 ID</dt>
            <dd>{assignment.currentAssigneeId}</dd>
            <dt className="text-muted-foreground">市场用户 ID</dt>
            <dd>{assignment.marketUserId ?? '—'}</dd>
            <dt className="text-muted-foreground">市场团队 ID</dt>
            <dd>{assignment.marketTeamId ?? '—'}</dd>
          </dl>
        ) : (
          <p className="mt-2 text-sm text-muted-foreground">暂无分配信息</p>
        )}
      </Card>

      {/* Followups */}
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <h3 className="font-medium">跟进记录</h3>
          <Button variant="outline" size="sm" onClick={() => setFollowupCreateOpen(true)}>
            新建跟进
          </Button>
        </div>
        {followups.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">暂无跟进记录</p>
        ) : (
          <ul className="mt-3 space-y-3">
            {followups.map((f) => (
              <li key={f.id} className="rounded-md border p-3 text-sm">
                <div className="flex items-start justify-between">
                  <div>
                    <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
                      {FOLLOWUP_TYPE_LABELS[f.followupType]}
                    </span>
                    <p className="mt-1">{f.content}</p>
                    {f.estimatedAmount && (
                      <p className="mt-1 text-muted-foreground">预估金额：{f.estimatedAmount}</p>
                    )}
                  </div>
                  <button
                    className="ml-3 text-xs text-primary hover:underline"
                    onClick={() => setEditFollowup(f)}
                  >
                    编辑
                  </button>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {new Date(f.createdAt).toLocaleString('zh-CN')}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Handoffs */}
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <h3 className="font-medium">移交记录</h3>
          <Button variant="outline" size="sm" onClick={() => setHandoffCreateOpen(true)}>
            发起移交
          </Button>
        </div>
        {handoffs.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">暂无移交记录</p>
        ) : (
          <ul className="mt-3 space-y-3">
            {handoffs.map((h) => (
              <li key={h.id} className="rounded-md border p-3 text-sm">
                <div className="flex items-start justify-between">
                  <div>
                    <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
                      {HANDOFF_STATUS_LABELS[h.status]}
                    </span>
                    <p className="mt-1">
                      {h.fromUserId} → {h.toUserId}
                    </p>
                    {h.reason && <p className="mt-1 text-muted-foreground">原因：{h.reason}</p>}
                  </div>
                  {h.status === 'pending' && (
                    <div className="ml-3 flex gap-2">
                      <button
                        className="text-xs text-green-600 hover:underline"
                        onClick={() => void handleConfirmHandoff(h)}
                      >
                        确认
                      </button>
                      <button
                        className="text-xs text-destructive hover:underline"
                        onClick={() => void handleRejectHandoff(h)}
                      >
                        拒绝
                      </button>
                    </div>
                  )}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {new Date(h.createdAt).toLocaleString('zh-CN')}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Sources */}
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <h3 className="font-medium">来源记录</h3>
          <Button variant="outline" size="sm" onClick={() => setSourceCreateOpen(true)}>
            新建来源
          </Button>
        </div>
        {sources.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">暂无来源记录</p>
        ) : (
          <ul className="mt-3 space-y-2">
            {sources.map((s) => (
              <li key={s.id} className="rounded-md border p-3 text-sm">
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
                  {SOURCE_TYPE_LABELS[s.sourceType]}
                </span>
                {s.sourceDetail && (
                  <pre className="mt-1 overflow-auto text-xs text-muted-foreground">
                    {s.sourceDetail}
                  </pre>
                )}
                <p className="mt-1 text-xs text-muted-foreground">
                  {new Date(s.createdAt).toLocaleString('zh-CN')}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Customer Account */}
      <CustomerAccountPanel customerId={customerId} />

      {/* Intake assessment / golden analysis / deal intention */}
      <CustomerIntakeAssessmentPanel customerId={customerId} />

      {/* Quote / contract — Issue #327 */}
      <CustomerQuoteContractPanel customerId={customerId} />

      {/* Dialogs — each keyed to force remount on open */}
      {editDialogOpen && (
        <EditCustomerDialog
          key={`edit-${customer.id}-${customer.version}`}
          customer={customer}
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          onSaved={(updated) => setCustomer(updated)}
        />
      )}

      {stageDialogOpen && (
        <StageTransitionDialog
          key={`stage-${customer.id}-${customer.version}`}
          customer={customer}
          open={stageDialogOpen}
          onOpenChange={setStageDialogOpen}
          onTransitioned={(updated) => setCustomer(updated)}
        />
      )}

      {followupCreateOpen && (
        <CreateFollowupDialog
          key={`followup-create-${customerId}`}
          customerId={customerId}
          open={followupCreateOpen}
          onOpenChange={setFollowupCreateOpen}
          onCreated={(f) => setFollowups((prev) => [f, ...prev])}
        />
      )}

      {editFollowup && (
        <EditFollowupDialog
          key={`followup-edit-${editFollowup.id}-${editFollowup.version}`}
          followup={editFollowup}
          open={!!editFollowup}
          onOpenChange={(open) => { if (!open) setEditFollowup(null); }}
          onSaved={(updated) => {
            setFollowups((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));
            setEditFollowup(null);
          }}
        />
      )}

      {handoffCreateOpen && (
        <CreateHandoffDialog
          key={`handoff-create-${customerId}`}
          customerId={customerId}
          open={handoffCreateOpen}
          onOpenChange={setHandoffCreateOpen}
          onCreated={(h) => setHandoffs((prev) => [h, ...prev])}
        />
      )}

      {sourceCreateOpen && (
        <CreateSourceDialog
          key={`source-create-${customerId}`}
          customerId={customerId}
          open={sourceCreateOpen}
          onOpenChange={setSourceCreateOpen}
          onCreated={(s) => setSources((prev) => [s, ...prev])}
        />
      )}

      {assignmentDialogOpen && (
        <UpsertAssignmentDialog
          key={`assignment-${customerId}`}
          customerId={customerId}
          current={assignment}
          open={assignmentDialogOpen}
          onOpenChange={setAssignmentDialogOpen}
          onSaved={(a) => setAssignment(a)}
        />
      )}
    </div>
  );
}
